import { useEffect, useMemo, useReducer } from 'react';

import { useAppDispatch, useAppSelector } from '@src/core/hooks';
import { SellingMode, type DetailedSaleItem, type ScheduleTime } from '@src/core/buyerApiClient';
import {
  collectAvailableScheduleTimes,
  findRescheduleSchedulingForSelection,
  getNextRescheduleStep,
  getPreviousRescheduleStep,
  isRescheduleStepComplete,
  requiresAdditionalRescheduleCharge,
  resolveRescheduleActionLabelKey,
  resolveRescheduleChargeSummary,
} from '@src/domains/sale-items';
import reducer, { initialState, RescheduleActionType } from '@src/features/sales/reschedule/store/rescheduleReducer';
import {
  processReschedule,
  currentRescheduleSelector,
  saleItemRescheduleOngoingSelector,
  loadingSaleItemRescheduleOptionsSelector,
} from '@src/stores/sale';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';

interface Params {
  reservationId: string;
  saleItem: DetailedSaleItem;
  onConfirm: () => void;
}

export function useRescheduleFlow({ reservationId, saleItem, onConfirm }: Params) {
  const { product } = saleItem;
  const { isFeatureFlagEnabled } = useFeatureFlag();
  const dispatch = useAppDispatch();

  const schedulingsByDate = useAppSelector(currentRescheduleSelector(reservationId));
  const isRescheduleOngoing = useAppSelector(saleItemRescheduleOngoingSelector);
  const isLoadingOptions = useAppSelector(loadingSaleItemRescheduleOptionsSelector);

  const [flowState, flowDispatch] = useReducer(reducer, initialState);
  const { currentStep, steps, visible, date, time } = flowState;

  const isLastStep = useMemo(() => steps[steps.length - 1] === currentStep, [steps, currentStep]);
  const isWithDateOnly = useMemo(() => product.sellingMode === SellingMode.WithDateOnly, [product.sellingMode]);
  const isWithDateAndTime = useMemo(() => product.sellingMode === SellingMode.WithDateAndTime, [product.sellingMode]);

  const chargeSummary = useMemo(
    () => resolveRescheduleChargeSummary({ schedulingsByDate, date, time, isWithDateOnly }),
    [schedulingsByDate, date, time, isWithDateOnly],
  );

  const isChargedRescheduleEnabled = isFeatureFlagEnabled('PR-3691-charged-reschedule');

  const requiresAdditionalPayment = useMemo(
    () =>
      requiresAdditionalRescheduleCharge({
        isChargedRescheduleEnabled,
        isChargeable: saleItem.rescheduleInfo.chargeable,
        chargeSummary,
      }),
    [chargeSummary, saleItem.rescheduleInfo.chargeable, isChargedRescheduleEnabled],
  );

  const scheduleTimes = useMemo(
    () => (date ? collectAvailableScheduleTimes(schedulingsByDate[date] ?? []) : undefined),
    [date, schedulingsByDate],
  );

  const isStepComplete = useMemo(
    () => isRescheduleStepComplete({ isWithDateAndTime, time }),
    [isWithDateAndTime, time],
  );

  const actionLabelKey = useMemo(
    () => resolveRescheduleActionLabelKey({ isLastStep, requiresAdditionalPayment, isChargedRescheduleEnabled }),
    [isLastStep, requiresAdditionalPayment, isChargedRescheduleEnabled],
  );

  const resetFields = () => flowDispatch({ type: RescheduleActionType.ResetFields });

  const confirmReschedule = () => {
    if (date && (isWithDateOnly || isWithDateAndTime)) {
      const scheduling = findRescheduleSchedulingForSelection({ schedulingsByDate, date, time, isWithDateOnly });

      dispatch(processReschedule(reservationId, scheduling!, time?.minutes ?? null));
    }
  };

  const goToNextStep = () => {
    if (isLastStep) {
      flowDispatch({ type: RescheduleActionType.SetCurrentStep, payload: undefined });

      confirmReschedule();
      return;
    }

    flowDispatch({
      type: RescheduleActionType.SetCurrentStep,
      payload: getNextRescheduleStep({ currentStep: currentStep!, steps, isWithDateOnly }),
    });
  };

  const goToPreviousStep = () =>
    flowDispatch({
      type: RescheduleActionType.SetCurrentStep,
      payload: getPreviousRescheduleStep({ currentStep: currentStep!, steps, isWithDateOnly }),
    });

  const selectDate = (selectedDate: string) => {
    flowDispatch({ type: RescheduleActionType.SelectDate, payload: selectedDate });

    goToNextStep();
  };

  const selectTime = (selectedTime: ScheduleTime) =>
    flowDispatch({ type: RescheduleActionType.SelectTime, payload: selectedTime });

  const cancel = () => {
    resetFields();
  };

  useEffect(() => {
    if (!isRescheduleOngoing && !currentStep) {
      onConfirm();
      resetFields();
    }
  }, [isRescheduleOngoing]);

  return {
    currentStep,
    steps,
    visible,
    date,
    time,
    schedulingsByDate,
    scheduleTimes,
    chargeSummary,
    requiresAdditionalPayment,
    isLastStep,
    isStepComplete,
    isLoadingOptions,
    isRescheduleOngoing,
    actionLabelKey,
    selectDate,
    selectTime,
    goToNextStep,
    goToPreviousStep,
    cancel,
  };
}
